import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { JVInput } from "../../shared/JVInput";
import { JVDropdownInput } from "../../shared/JVDropdownInput";
import { TRequirementRecord, JobTypeOptions, ReqPriorityOptions, ReqTermOptions, ReqTypeOptions, JobType } from "./modal";
import { TDropdown } from "../../shared/modal";
import { Fetch } from "../../utilities/fetch";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVMultiselect } from "../../shared/JVMultiselect";
import { useToast } from "../../context/toastProvider";
import { JVSunEditor } from "../../shared/JVSuneditor";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { format } from "date-fns";
import { RadioButton } from "primereact/radiobutton";

const schema = yup.object({
    jobTitle: yup.string().required("Job Title is required*")
        .min(3, "Job Title must be at least 3 characters*")
        .max(250, "Job Title must be at most 250 characters*"),
    jobType: yup.number().min(1, "Job Type is required*").required("Job Type is required*"),
    requiredSkill: yup.string().required("Required Skill is required*"),
    rate: yup.number().typeError("Rate is required*").required("Rate is required*"),
    duration: yup.number().typeError("Duration is required*").min(1, "Duration is required*").required("Duration is required*"),
    industryId: yup.number().min(1, "Industry is required*").required("Industry is required*"),
    skillCategoryId: yup.number().min(1, "Skill Category is required*").required("Skill Category is required*"),
    interviewProcessId: yup.number().min(1, "Interview Process is required*").required("Interview Process is required*"),
    countryId: yup
        .number()
        .when('jobType', {
            is: (val: number) => val !== JobType.REMOTE,
            then: (schema) =>
                schema.min(1, "Country is required*").required("Country is required*"),
            otherwise: (schema) => schema.notRequired()
        }),

    state: yup
        .object()
        .when('jobType', {
            is: (val: number) => val !== JobType.REMOTE,
            then: () =>
                yup.object({
                    id: yup.number().min(1, "State is required*").required("State is required*"),
                    name: yup.string().required()
                }),
            otherwise: () => yup.object().nullable()
        }),

    city: yup
        .object()
        .when('jobType', {
            is: (val: number) => val !== JobType.REMOTE,
            then: () =>
                yup.object({
                    id: yup.number().min(1, "City is required*").required("City is required*"),
                    name: yup.string().required()
                }),
            otherwise: () => yup.object().nullable()
        }),
    term: yup.number().min(1, "Term is required*").required("Term is required*"),
    reqType: yup.number().min(1, "Requirement Type is required*").required("Requirement Type is required*"),
    priority: yup.number().min(1, "Priority is required*").required("Priority is required*"),
});

interface Iprops {
    data: TRequirementRecord;
    open: boolean;
    hideDialog: () => void;
    reload: () => void;
}

export interface IVendorCompanyInfo {
    vendorCompanyID: number;
    noOfReq: number;
    noOfSub: number;
    noOfECI: number;
    noOfPo: number;
    blacklisted: boolean;
    blackReason: string;
}
export interface IClient {
    contactPersonId: number;
    contactPersonName: string;
    contactPersonEmail: string;
    phoneNo: string;
    vendorCompanyId: number;
    vendorCompanyName: string;
    createdOn: string;
    noOfReq: number;
    noOfServe: number;
    noOfSub: number;
    noEndClient: number;
    noOfECI: number;
    noOfPO: number;
}

export const Form = (props: Iprops) => {
    const { showToast } = useToast();
    const [industries, setIndustries] = useState<Array<TDropdown>>();
    const [skillCategories, setSkillCategories] = useState<Array<TDropdown>>();
    const [interviewProcess, setInterviewProcess] = useState<Array<TDropdown>>();

    const [clientCompanies, setClientCompanies] = useState<Array<TDropdown>>();
    const [clients, setClients] = useState({ vendorCompanyInfo: undefined, clients: [], loading: false } as { vendorCompanyInfo?: IVendorCompanyInfo, clients: IClient[], loading: boolean, selectedClient?: number });

    const [visa, setVisa] = useState<Array<TDropdown>>();
    const [selectedVisa, setSelectedVisa] = useState<Array<TDropdown>>();
    const [countries, setCountries] = useState<Array<TDropdown>>();
    const [states, setStates] = useState<Array<TDropdown>>();
    const [cities, setCities] = useState<Array<TDropdown>>();

    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);

    const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<TRequirementRecord>({
        resolver: yupResolver(schema),
        defaultValues: props.data,
        shouldFocusError: false
    });

    useEffect(() => {
        reset(props.data);
        setDescription(props.data.jobDescription);
        setSelectedVisa(props.data.sVisa);
        if (props.data.contactPersonId) {
            searchClients(props.data.contactPersonId);
        } else {
            setClients({ vendorCompanyInfo: undefined, clients: [], loading: false, selectedClient: undefined });
        }
    }, [props.data]);

    const loadCountries = async () => {
        const data = await Fetch<TDropdown[]>(`dropdown/countries`);
        if (data) {
            setCountries(data);
        }
    }

    const loadIndustries = async () => {
        const data = await Fetch<TDropdown[]>("dropdown/industries");
        if (data) {
            setIndustries(data);
        }
    }
    const loadInterviewProcess = async () => {
        const data = await Fetch<TDropdown[]>("dropdown/interviewProcess");
        if (data) {
            setInterviewProcess(data);
        }
    }
    const loadSkillCategories = async () => {
        const data = await Fetch<TDropdown[]>("dropdown/skillCategories");
        if (data) {
            setSkillCategories(data);
        }
    }
    const loadVisa = async () => {
        const data = await Fetch<TDropdown[]>("dropdown/visa");
        if (data) {
            setVisa(data);
        }
    }

    const countryId = watch('countryId');
    const state = watch('state');
    const clientCompany = watch('clientCompany');
    const jobType = watch('jobType');

    const loadClientCompanies = async (filter: string | null) => {
        const data = await Fetch<TDropdown[]>("dropdown/vendorCompanies", {
            query: `filter=${filter}`
        });
        if (data) {
            setClientCompanies(data);
        }
    }
    const completeMethodClientCompanies = (event: { query: string }) => {
        loadClientCompanies(event.query);
    }

    // const loadContactPersons = async (filter: string | null) => {
    //     if (clientCompany?.id) {
    //         const data = await Fetch<TDropdown[]>("dropdown/vendorContactPersons", {
    //             query: `vendorCompanyId=${clientCompany.id}&filter=${filter}`
    //         });
    //         if (data) {
    //             setContactPersons(data);
    //         }
    //     } else {
    //         showToast({ severity: 'error', detail: "Select the client company first.", life: 3000 });
    //     }
    // }
    // const completeMethodContactPersons = (event: { query: string }) => {
    //     loadContactPersons(event.query);
    // }

    const loadStates = async (filter: string | null) => {
        if (countryId) {
            const data = await Fetch<TDropdown[]>("dropdown/states",
                { query: `countryId=${countryId}&filter=${filter}` });
            if (data) {
                setStates(data);
            }
        } else {
            showToast({ severity: 'error', detail: "Select the Country First.", life: 3000 });
        }
    }
    const completeMethodState = (event: { query: string }) => {
        loadStates(event.query);
    }

    const loadCities = async (filter: string | null) => {
        if (state?.id) {
            const data = await Fetch<TDropdown[]>("dropdown/cities", {
                query: `stateId=${state.id}&filter=${filter}`
            });
            if (data) {
                setCities(data);
            }
        } else {
            showToast({ severity: 'error', detail: "Select the State First.", life: 3000 });
        }
    }
    const completeMethodCity = (event: { query: string }) => {
        loadCities(event.query);
    }

    useEffect(() => {
        loadCountries();
        loadIndustries();
        loadSkillCategories();
        loadInterviewProcess();
        loadVisa();
    }, []);

    const onHide = () => {
        props.hideDialog();
        setSelectedVisa(undefined);
        setDescription('');
    }

    const onSubmit = async (data: TRequirementRecord) => {
        if (loading) return;
        if (!clients.selectedClient) {
            showToast({ severity: 'error', detail: "Please select a contact person.", life: 3000 });
            return;
        }

        setLoading(true);

        const id = props.data.id;
        const method = id ? "PUT" : "POST";
        const message = id ? "Updated Successfully" : "Added Successfully";

        data.contactPersonId = clients.selectedClient;
        data.cityId = data.jobType !== JobType.REMOTE ? data.city?.id : undefined;
        data.visa = selectedVisa?.map(v => v.id) ?? [];
        data.jobDescription = description;

        const response = await Fetch(id ? `SalesMyRequirement/${id}` : 'SalesMyRequirement', {
            method,
            body: data,
            onError: (m) => showToast({ severity: 'error', detail: m })
        });

        if (response) {
            props.hideDialog();
            props.reload();
            showToast({ severity: 'success', detail: message });
        }

        setLoading(false);
    }

    const searchClients = async (client: number | undefined = undefined) => {
        if (typeof client !== 'number') client = undefined;
        const clientCompanyId = props.data.clientCompany?.id || clientCompany?.id;
        if (clientCompanyId) {
            setClients({ clients: [], loading: true, selectedClient: client });
            const data = await Fetch<{ vendorCompanyInfo: IVendorCompanyInfo, clients: IClient[] }>(`SalesMyRequirement/${clientCompanyId}/clients`);
            if (data) {
                setClients({ vendorCompanyInfo: data.vendorCompanyInfo, clients: data.clients, loading: false, selectedClient: client });
                if (data.clients.length === 0) {
                    showToast({ severity: 'error', detail: "No Client Found", life: 3000 });
                }
            } else {
                setClients({ vendorCompanyInfo: undefined, clients: [], loading: false, selectedClient: client });
            }
        } else {
            showToast({ severity: 'error', detail: "Please Type a Valid Client", life: 3000 });
        }
    }

    const radioBodyTemplate = (rowData: IClient) => {
        return (
            <RadioButton inputId={rowData.contactPersonId.toString()}
                name={'contactPersonId'}
                value={rowData.contactPersonId}
                onChange={(e) => {
                    setClients(prev => ({ ...prev, selectedClient: e.value }))
                }}
                checked={(clients.selectedClient === rowData.contactPersonId)} />
        );
    }

    return (
        <Dialog visible={props.open} style={{ width: '80vw' }} header="Add/Edit Requirement" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">

                <JVDropdownInput icon="search" iconbg="warning" func={searchClients} required completeMethod={completeMethodClientCompanies} label="Client" control={control} name="clientCompany" error={errors.clientCompany?.id?.message} options={clientCompanies} optionLabel="name" placeholder="Search Client/Company" />
                {clients.vendorCompanyInfo && <>
                    <div className="field col-12 md:col-6" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', }}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '35px', width: '35px', border: "2px solid #ccc", borderRadius: '50%', margin: '0px 5px', background: clients.vendorCompanyInfo.blacklisted ? 'red' : 'green' }}></span>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '35px', width: '35px', border: "2px solid #ccc", borderRadius: '50%', margin: '0px 5px' }}>{clients.vendorCompanyInfo.noOfReq}</span>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '35px', width: '35px', border: "2px solid #ccc", borderRadius: '50%', margin: '0px 5px' }}>{clients.vendorCompanyInfo.noOfSub}</span>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '35px', width: '35px', border: "2px solid #ccc", borderRadius: '50%', margin: '0px 5px' }}>{clients.vendorCompanyInfo.noOfECI}</span>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '35px', width: '35px', border: "2px solid #ccc", borderRadius: '50%', margin: '0px 5px' }}>{clients.vendorCompanyInfo.noOfPo}</span>
                    </div>
                    {(clients.vendorCompanyInfo && clients.vendorCompanyInfo.blacklisted === true) && <div className="field col-12 md:col-3 -ml-2 text-xl font-bold flex flex-row-reverse w-full"><p style={{ color: 'red' }} className="">{clients.vendorCompanyInfo.blackReason}</p></div>}
                </>}
                <div style={{ marginTop: '10px', marginBottom: '20px', width: '100%' }}>
                    <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0", display: `${clients.clients?.length > 0 ? "block" : "none"}` }}
                        size="small" value={clients.clients}>
                        <Column header={<small className="p-error" >{errors.contactPersonId?.message}</small>} body={radioBodyTemplate} />
                        <Column field="contactPersonName" header="Contact Person" />
                        <Column field="vendorCompanyName" header="Company Name" />
                        <Column field="phoneNo" header="Phone No" />
                        <Column field="createdOn" header="Added Date" body={(rowData: IClient) => format(new Date(rowData.createdOn), 'dd/MM/yyyy')} />
                        <Column field="noOfReq" header="Req#" sortable />
                        <Column field="noOfServe" header="Serv#" sortable />
                        <Column field="noOfSub" header="Sub#" sortable />
                        <Column field="noEndClient" header="ECS#" sortable />
                        <Column field="noOfECI" header="ECI#" sortable />
                        <Column field="noOfPO" header="PO#" sortable />
                    </DataTable>
                </div>

                {/* <JVDropdownInput required column={2} completeMethod={completeMethodClientCompanies} label="Client Company" control={control} name="clientCompany" error={errors.clientCompany?.id?.message}
                    options={clientCompanies} optionLabel="name" placeholder="Search client company" />
                <JVDropdownInput required column={2} completeMethod={completeMethodContactPersons} label="Contact Person" control={control} name="contactPerson" error={errors.contactPerson?.id?.message}
                    options={contactPersons} optionLabel="name" placeholder="Search contact person" /> */}

                <JVInput column={1} required label="Job Title" control={control} name="jobTitle" error={errors.jobTitle?.message} />
                <JVInput required label="Required Skill" control={control} name="requiredSkill" error={errors.requiredSkill?.message} />
                <JVInput required type="number" label="Rate" control={control} name="rate" error={errors.rate?.message} />
                <JVInput required type="number" label="Duration (Months)" control={control} name="duration" error={errors.duration?.message} />

                <JVDropdown required label="No of Positions" control={control} name="noOfPositions" error={errors.noOfPositions?.message}
                    options={[{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }, { id: 5 }, { id: 6 }, { id: 7 }, { id: 8 }, { id: 9 }, { id: 10 },
                    { id: 11 }, { id: 12 }, { id: 13 }, { id: 14 }, { id: 15 }, { id: 16 }, { id: 17 }, { id: 18 }, { id: 19 }, { id: 20 }
                    ]} optionLabel="id" optionValue="id" placeholder="Select no of positions" />

                <JVDropdown required label="Priority" control={control} name="priority" error={errors.priority?.message}
                    options={ReqPriorityOptions} optionLabel="name" optionValue="id" placeholder="Select priority" />

                <JVDropdown required label="Job Type" control={control} name="jobType" error={errors.jobType?.message}
                    options={JobTypeOptions} optionLabel="name" optionValue="id" placeholder="Select job type" />

                {jobType !== JobType.REMOTE && <>
                    <JVDropdown required column={3} label="Country" control={control} name="countryId" optionLabel="name" optionValue="id" options={countries} placeholder="Select country" error={errors.countryId?.message} />
                    <JVDropdownInput required column={3} completeMethod={completeMethodState} label="State" control={control} name="state" error={errors.state?.id?.message} options={states} optionLabel="name" placeholder="Search state" />
                    <JVDropdownInput required column={3} completeMethod={completeMethodCity} label="City" control={control} name="city" error={errors.city?.id?.message} options={cities} optionLabel="name" placeholder="Search city" />
                </>}

                <JVDropdown required label="Term" control={control} name="term" error={errors.term?.message}
                    options={ReqTermOptions} optionLabel="name" optionValue="id" placeholder="Select term" />
                <JVDropdown required label="Req Type" control={control} name="reqType" error={errors.reqType?.message}
                    options={ReqTypeOptions} optionLabel="name" optionValue="id" placeholder="Select req type" />

                <JVDropdown required label="Industry" control={control} name="industryId" error={errors.industryId?.message}
                    options={industries} optionLabel="name" optionValue="id" placeholder="Select industry" />
                <JVDropdown required label="Skill Category" control={control} name="skillCategoryId" error={errors.skillCategoryId?.message}
                    options={skillCategories} optionLabel="name" optionValue="id" placeholder="Select skill category" />
                <JVDropdown required label="Interview Process" control={control} name="interviewProcessId" error={errors.interviewProcessId?.message}
                    options={interviewProcess} optionLabel="name" optionValue="id" placeholder="Select interview process" />
                <JVMultiselect label="Visa" options={visa} optionLabel="name" placeholder="Select visa" required value={selectedVisa} onChange={setSelectedVisa} />
                <div className="col-12 md:col-12 md:col-6">
                    <label htmlFor="jobDescription">Job Description<span style={{ color: 'red' }}>*</span></label>
                    <JVSunEditor name="jobDescription" value={description} handleChange={(t) => { setDescription(t); }} />
                </div>
                <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                    <Button style={{ width: '100px' }} label="SAVE" type="submit" loading={loading}></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-outlined surface-200"></Button>
                </div>

            </form>
        </Dialog>
    );
}
